import { c as _c } from "react/compiler-runtime";
import { ComponentProps, useState } from 'react';
import { useCopyToClipboard } from '../../utils/copy';
import Icon from './base/Icon';
import Text from './base/Text';
interface Props extends Omit<ComponentProps<typeof Text>, 'children'> {
  /** Text copied to the clipboard. Also rendered, unless `children` overrides the display. */
  value: string;
  /** Display override — e.g. wrapping `value` in parentheses. */
  children?: React.ReactNode;
  /** Wrap onto multiple lines instead of truncating with an ellipsis. */
  wrap?: boolean;
  /** Reveal the copy icon on hover instead of always showing it. */
  icon_on_hover?: boolean;
}

/**
 * Inline click-to-copy text: truncates with an ellipsis (or wraps) and shows a copy icon.
 * The icon is rendered inside the `Text` so it flows with the content instead of being
 * positioned by the parent flex container. For the boxed/labelled variant, use `CopyableField`.
 */
const CopyableText: React.FC<Props> = t0 => {
  const $ = _c(28);
  let children;
  let props;
  let style;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      children,
      wrap: t1,
      icon_on_hover: t2,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = style;
    $[4] = t1;
    $[5] = t2;
    $[6] = value;
  } else {
    children = $[1];
    props = $[2];
    style = $[3];
    t1 = $[4];
    t2 = $[5];
    value = $[6];
  }
  const wrap = t1 === undefined ? false : t1;
  const icon_on_hover = t2 === undefined ? false : t2;
  const copyToClipboard = useCopyToClipboard();
  const [is_hovered, setIsHovered] = useState(false);
  const t3 = !wrap;
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsHovered(true);
    t5 = () => setIsHovered(false);
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== copyToClipboard || $[10] !== value) {
    t6 = e => {
      copyToClipboard(value);
      e.stopPropagation();
    };
    $[9] = copyToClipboard;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== wrap) {
    t7 = wrap ? {
      overflowWrap: "anywhere",
      whiteSpace: "normal"
    } : undefined;
    $[12] = wrap;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== style || $[15] !== t7) {
    t8 = {
      cursor: "pointer",
      ...t7,
      ...style
    };
    $[14] = style;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = children ?? value;
  let t10;
  if ($[17] !== icon_on_hover || $[18] !== is_hovered) {
    t10 = (!icon_on_hover || is_hovered) && <Icon icon="copy" small={true} pointer={true} muted={true} right={1} />;
    $[17] = icon_on_hover;
    $[18] = is_hovered;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== props || $[21] !== t10 || $[22] !== t3 || $[23] !== t6 || $[24] !== t8 || $[25] !== t9 || $[26] !== value) {
    t11 = <Text ellipsis={t3} min_w={0} title={value} {...props} onMouseEnter={t4} onMouseLeave={t5} onClick={t6} style={t8}>{t9}{t10}</Text>;
    $[20] = props;
    $[21] = t10;
    $[22] = t3;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
    $[26] = value;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
export default CopyableText;